<template>
  <div class="setup-page">
    <header class="setup-page-header">
      <div class="setup-brand">
        <img :src="brand.assets.logoMark" :alt="brand.productName" />
        <div>
          <span>Workspace configuration</span>
          <strong>{{ brand.productName }}</strong>
        </div>
      </div>
      <div class="setup-progress-copy">
        <span>Step {{ currStep || 1 }} of {{ steps.length || 7 }}</span>
        <strong>{{ currentStepTitle }}</strong>
      </div>
    </header>

    <div class="setup-layout">
      <aside class="setup-steps" aria-label="Setup progress">
        <button
          v-for="(step, index) in steps"
          :key="step.id"
          type="button"
          class="setup-step"
          :class="{ 'is-active': step.id == currStep, 'is-complete': step.isComplete }"
          :aria-current="step.id == currStep ? 'step' : undefined"
          @click="handleActive(step.id)"
        >
          <span class="setup-step-index">{{ step.isComplete ? "OK" : index + 1 }}</span>
          <span>
            <strong>{{ step.title }}</strong>
            <small>{{ step.isComplete ? "Complete" : step.id == currStep ? "In progress" : "Pending" }}</small>
          </span>
        </button>
      </aside>

      <section class="setup-content">
        <HmoSetup v-if="currStep == 1" @next="handleNext" />
        <SchemeSetup v-if="currStep == 2" @next="handleNext" />
        <PlanSetup v-if="currStep == 3" @next="handleNext" />
        <TariffSetup v-if="currStep == 4" @next="handleNext" />
        <ProviderSetup v-if="currStep == 5" @next="handleNext" />
        <SettingSetup v-if="currStep == 6" @next="handleNext" />
        <FinalSetup v-if="currStep == 7" @next="handleNext" />
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
// import { useRouter } from "vue-router";
import HmoSetup from "@/components/setup/HmoSetupComponent.vue";
import SchemeSetup from "@/components/setup/SchemeSetupComponent.vue";
import PlanSetup from "@/components/setup/PlanSetupComponent.vue";
import TariffSetup from "@/components/setup/TariffSetupComponent.vue";
import ProviderSetup from "@/components/setup/ProviderSetupComponent.vue";
import SettingSetup from "@/components/setup/SettingSetupComponent.vue";
import FinalSetup from "@/components/setup/FinalSetupComponent.vue";
import brand from "@/whitelabel/brand.config";


export default {
  name: "setupHome",
  components: { HmoSetup, SchemeSetup, PlanSetup, TariffSetup, ProviderSetup, SettingSetup, FinalSetup },
  setup() {
    // Workmen
    const error = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);
    const steps = ref([]);
    const currStep = ref(null);
    const currentStepTitle = computed(() => steps.value.find((step) => step.id == currStep.value)?.title || "Getting started");

    // router
    // const router = useRouter();

    onMounted(() => {
      // page name
      document.title = `Setup | ${brand.productName}`;
    });

    // Initiate
    onMounted(() => {
      // load tab
      steps.value.push({ id: 1, title: "HMO", isComplete: false });
      steps.value.push({ id: 2, title: "Schemes", isComplete: false });
      steps.value.push({ id: 3, title: "Plans", isComplete: false });
      steps.value.push({ id: 4, title: "Tariffs", isComplete: false });
      steps.value.push({ id: 5, title: "Providers", isComplete: false });
      steps.value.push({ id: 6, title: "Settings", isComplete: false });
      steps.value.push({ id: 7, title: "Finish", isComplete: false });

      // set default
      currStep.value = 1;
    });

    // handle curr status
    const handleActive = (tab) => {
      // update show and components
      currStep.value = tab;
    };

    // handle next // emitted parameters
    const handleNext = (tab) => {
      // update steps
      steps.value.forEach((x) => {
        // sort and update
        if (x.id == tab.step) {
          x.isComplete = tab.isComplete;
        }
      });
      // next available stap
      let next = null;
      steps.value.forEach((x) => {
        // sort and update
        if (!x.isComplete && !next) {
          next = x.id;
        }
      });
      // update show and components
      currStep.value = next;
    };


    return {
      brand,
      currentStepTitle,
      handleActive,
      handleNext,
      error,
      isReady,
      isSubmitting,
      steps,
      currStep,
    };
  },
};
</script>

<style scoped>
.setup-page { background: var(--ui-muted); min-height: 100vh; }
.setup-page-header {
  align-items: center;
  background: var(--ui-background);
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  justify-content: space-between;
  min-height: var(--ui-header-height);
  padding: 0.75rem clamp(1rem, 3vw, 2rem);
}
.setup-brand, .setup-progress-copy { align-items: center; display: flex; gap: 0.75rem; }
.setup-brand img { height: 2.5rem; width: 2.5rem; }
.setup-brand div, .setup-progress-copy { display: flex; flex-direction: column; }
.setup-brand span, .setup-progress-copy span { color: var(--ui-muted-foreground); font-size: 0.7rem; }
.setup-brand strong, .setup-progress-copy strong { color: var(--ui-foreground); font-size: 0.84rem; }
.setup-progress-copy { align-items: flex-end; }
.setup-layout {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 15rem minmax(0, 1fr);
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 1.5rem;
}
.setup-steps {
  align-self: start;
  background: var(--ui-card);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow-xs);
  display: grid;
  gap: 0.25rem;
  padding: 0.5rem;
  position: sticky;
  top: 1.5rem;
}
.setup-step {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-muted-foreground);
  cursor: pointer;
  display: flex;
  gap: 0.7rem;
  padding: 0.65rem;
  text-align: left;
  width: 100%;
}
.setup-step:hover, .setup-step.is-active { background: var(--ui-accent); color: var(--ui-accent-foreground); }
.setup-step-index {
  align-items: center;
  background: var(--ui-secondary);
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 1.8rem;
  font-size: 0.72rem;
  font-weight: 700;
  height: 1.8rem;
  justify-content: center;
}
.setup-step.is-active .setup-step-index { background: var(--ui-primary); color: var(--ui-primary-foreground); }
.setup-step.is-complete .setup-step-index { color: var(--ui-success); }
.setup-step strong, .setup-step small { display: block; }
.setup-step strong { color: inherit; font-size: 0.8rem; }
.setup-step small { font-size: 0.68rem; margin-top: 0.12rem; }
.setup-content { min-width: 0; }

@media (max-width: 900px) {
  .setup-layout { grid-template-columns: 1fr; padding: 1rem; }
  .setup-steps {
    display: flex;
    overflow-x: auto;
    position: static;
    scrollbar-width: thin;
  }
  .setup-step { flex: 0 0 auto; min-width: 8.5rem; }
}
@media (max-width: 600px) {
  .setup-progress-copy { display: none; }
  .setup-brand span { display: none; }
}
</style>
